import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiImage, FiCrop, FiCheck, FiSearch } from 'react-icons/fi';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios from '../config/axios';

const GalleryImageSelector = ({ isOpen, onClose, onSelect, currentImage }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'general', label: 'General' },
    { value: 'nature', label: 'Nature' },
    { value: 'urban', label: 'Urban' },
    { value: 'historic', label: 'Historic' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'water', label: 'Water' },
    { value: 'park', label: 'Park' },
    { value: 'event', label: 'Event' },
    { value: 'people', label: 'People' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadImages();
    }
  }, [isOpen, search, category]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !showCropModal) {
        onClose();
      }
    };

    if (isOpen && !showCropModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, showCropModal, onClose]);

  const loadImages = async () => {
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search) params.search = search;

      const response = await axios.get('/cms/gallery', { params });
      setImages(response.data);
    } catch (error) {
      console.error('Error loading gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (image) => {
    setSelectedImage(image);
    setShowCropModal(true);
  };

  const handleSelectWithoutCrop = (image) => {
    onSelect({
      id: image.id,
      url: image.image_url,
      crop: null
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Select Hero Background</h2>
              <p className="text-sm text-gray-600 mt-1">Choose an image from your gallery</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search images..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Image Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-12">
                <FiImage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No images found</p>
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters or upload images to the gallery</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map(image => (
                  <div
                    key={image.id}
                    className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                    onClick={() => handleImageSelect(image)}
                  >
                    <img
                      src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageSelect(image);
                          }}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          <FiCrop className="w-4 h-4" />
                          Crop & Select
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectWithoutCrop(image);
                          }}
                          className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2"
                        >
                          <FiCheck className="w-4 h-4" />
                          Use As-Is
                        </button>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-sm font-medium truncate">{image.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Info */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="text-sm text-gray-600">
              <p><strong>Tip:</strong> Click "Crop & Select" to adjust the image framing, or "Use As-Is" to use the full image.</p>
              <p className="mt-1">Selected images will be stored with crop settings for consistent display.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <ImageCropModal
          image={selectedImage}
          onClose={() => {
            setShowCropModal(false);
            setSelectedImage(null);
          }}
          onSave={(cropData) => {
            onSelect({
              id: selectedImage.id,
              url: selectedImage.image_url,
              crop: cropData
            });
            setShowCropModal(false);
            setSelectedImage(null);
            onClose();
          }}
        />
      )}
    </>
  );
};

// Image Crop Modal Component
const ImageCropModal = ({ image, onClose, onSave }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 100,
    height: 50,
    x: 0,
    y: 25
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);

  // ESC key handler for crop modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSave = () => {
    if (completedCrop) {
      // Get the actual image dimensions
      const imageWidth = imgRef.current?.naturalWidth || imgRef.current?.width || 1;
      const imageHeight = imgRef.current?.naturalHeight || imgRef.current?.height || 1;

      // Convert to percentages if in pixel units
      let cropData;
      if (completedCrop.unit === 'px') {
        cropData = {
          unit: '%',
          x: (completedCrop.x / imageWidth) * 100,
          y: (completedCrop.y / imageHeight) * 100,
          width: (completedCrop.width / imageWidth) * 100,
          height: (completedCrop.height / imageHeight) * 100
        };
      } else {
        cropData = {
          unit: '%',
          x: completedCrop.x,
          y: completedCrop.y,
          width: completedCrop.width,
          height: completedCrop.height
        };
      }

      onSave(cropData);
    } else {
      onSave(null);
    }
  };

  const presetCrops = [
    { name: 'Full Image', crop: { unit: '%', width: 100, height: 100, x: 0, y: 0 } },
    { name: 'Top Half', crop: { unit: '%', width: 100, height: 50, x: 0, y: 0 } },
    { name: 'Center', crop: { unit: '%', width: 100, height: 50, x: 0, y: 25 } },
    { name: 'Bottom Half', crop: { unit: '%', width: 100, height: 50, x: 0, y: 50 } },
    { name: 'Left Half', crop: { unit: '%', width: 50, height: 100, x: 0, y: 0 } },
    { name: 'Right Half', crop: { unit: '%', width: 50, height: 100, x: 50, y: 0 } }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Crop Hero Background</h2>
            <p className="text-sm text-gray-600 mt-1">{image.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Preset Buttons */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Presets:</p>
          <div className="flex flex-wrap gap-2">
            {presetCrops.map((preset) => (
              <button
                key={preset.name}
                onClick={() => setCrop(preset.crop)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-primary-50 hover:border-primary-500 hover:text-primary-700 transition-colors"
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {/* Crop Area */}
        <div className="flex-1 overflow-auto p-6 bg-gray-900 flex items-center justify-center">
          <div className="max-w-full max-h-full">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
              className="max-w-full max-h-[60vh]"
            >
              <img
                ref={imgRef}
                src={`${process.env.REACT_APP_API_URL.replace('/api', '')}${image.image_url}`}
                alt={image.title}
                className="max-w-full max-h-[60vh] object-contain"
              />
            </ReactCrop>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <p><strong>Instructions:</strong> Drag the crop box or use presets above</p>
            <p className="mt-1">The selected area will be displayed as your hero background</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary flex items-center gap-2"
            >
              <FiCheck className="w-4 h-4" />
              Save & Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryImageSelector;
